/**
 * web/src/pages/GateReview.tsx
 * UI-06: Gate review page. Loads the workflow + selects the step by stepIndex,
 * branches on the gateData discriminator (`'data-gate'` vs `'email-gate'`),
 * and renders FormRenderer or EmailPreview accordingly. Email gates ALSO render
 * any extra non-envelope GateFields from step.gateSchema (e.g. the speaker
 * confirmation gate's hotelBlock select) via FormRenderer above the preview.
 *
 * Features:
 *   - Resubmit banner with prior rejection reason when the step has been
 *     rejected (D-4.4) — pre-fills the form with `gateData.lastEdits`.
 *   - Sticky action bar (handoff §Specifics line 162) — backdrop-blur, full-bleed.
 *   - Optimistic Approve / Reject mutations with success toast (D-2.2);
 *     VALIDATION_ERROR field errors map to RHF setError.
 *   - Draft autosave to localStorage (debounced 500ms via use-debounce);
 *     cleared on successful Approve/Reject (D-4.3).
 *   - Keyboard shortcuts: ⌘+Enter / Ctrl+Enter submits, ⌘+Shift+Enter opens
 *     the reject modal, Escape closes any open modal.
 *   - Mobile single-column layout (D-3.2): action bar pinned to bottom safe-area
 *     at sm; full-width form; pb-32 reserves space below the sticky bar.
 *
 * Route accepts BOTH legacy `/workflows/:id/gate/:step` and the queue-driven
 * `/approvals/:workflowId/:stepIndex` so the page is reachable from either entry.
 *
 * Completed/skipped steps render a read-only SENT view instead of the stale
 * gate form (which 409'd on submit): a green done banner plus — when the step
 * sent a tracked email that carries a follow-up plan — the D1 plan editor
 * (NudgeTimeline). Approving a nudge-seeding speaker email gate (invite /
 * confirmation) STAYS on this page, which flips into that sent view so the
 * reviewer sets up the chase right after sending (2026-07-14, Danny).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkflow } from '../hooks/queries/useWorkflow';
import { useTrackedEmails } from '../hooks/queries/useTrackedEmails';
import { useOpsEmailThread } from '../hooks/queries/useOpsEmailThread';
import { useInviteThreads } from '../hooks/queries/useInviteThreads';
import { InviteThreadPicker } from '../components/speaker/InviteThreadPicker';
import { useUpdateNudgePlan } from '../hooks/mutations/useUpdateNudgePlan';
import { useSendNudgeNow } from '../hooks/mutations/useSendNudgeNow';
import { useApproveGate } from '../hooks/mutations/useApproveGate';
import { useRejectGate } from '../hooks/mutations/useRejectGate';
import { useResubmitGate } from '../hooks/mutations/useResubmitGate';
import { useSkipGate } from '../hooks/mutations/useSkipGate';
import { useRegenerateGate } from '../hooks/mutations/useRegenerateGate';
import { RegenerateDialog, type RegenerateTarget } from '../components/approvals/RegenerateDialog';
import { useRevertTracker } from '../hooks/mutations/useRevertTracker';
import { useAuth } from '../hooks/useAuth';
import { useSetTopbar } from '../components/layout/Topbar';
import { FormRenderer, type JSONSchema } from '../components/gates/FormRenderer';
import { EmailPreview } from '../components/gates/EmailPreview';
import { readMondayCard, promotionCardPinned } from '../lib/promotionCard';
import { gatePinnedAttachments } from '../lib/pinnedAttachments';
import { EmailThreadViewer } from '../components/gates/EmailThreadViewer';
import { CopyRationaleCard, readCopyRationale } from '../components/gates/CopyRationaleCard';
import { OpsTemplatePicker } from '../components/gates/OpsTemplatePicker';
import { CardCandidatesPanel } from '../components/gates/CardCandidatesPanel';
import { NudgeTimeline } from '../components/speaker/NudgeTimeline';
import { RejectModal } from '../components/gates/RejectModal';
import { TrackerChangesPanel } from '../components/gates/TrackerChangesPanel';
import { findPlaceholders, uniquePlaceholderKeys } from '../lib/findPlaceholders';
import { workflowPath } from '../lib/workflowPath';
import { findSpeakerPlaceholders, uniqueSpeakerPlaceholders } from '../lib/speakerPlaceholders';
import { substituteSenderName } from '../lib/senderName';
import { ApproveConfirm } from '../components/gates/ApproveConfirm';
import { SkipConfirm } from '../components/gates/SkipConfirm';
import { Button } from '../components/shared/Button';
import { Select } from '../components/shared/Select';
import { Badge } from '../components/shared/Badge';
import { Skeleton } from '../components/shared/Skeleton';
import { Empty } from '../components/shared/Empty';
import { Icon } from '../lib/icons';
import { ApiClientError } from '../lib/api';
import { key, saveDraft, readDraftOnBase, clearDraft } from '../lib/forms/draft';
import { qk } from '../lib/queryKeys';
import { titleCase } from '../lib/format';
import { workflowHomeCrumb } from '../lib/workflow-step-defs';
import {
  readCardCandidates,
  readCardGateState,
  readTemplatePicker,
  type GateData,
  type StepExecution,
} from '../api/types';

/**
 * Backend gates emit `schema` as an array of GateField defs, but FormRenderer
 * expects a JSON Schema. Convert: each GateField becomes a JSONSchema property,
 * with `type` mapped to a JSON Schema type/format, and `editable: false`
 * fields collected into a readonly list.
 */
interface GateField {
  key: string;
  label: string;
  type: 'readonly' | 'number' | 'textarea' | 'date' | 'string' | 'email' | 'boolean' | 'select';
  editable: boolean;
  /** Choices for a `select` field — mapped to JSON Schema `enum` for a dropdown. */
  options?: readonly string[];
}

/**
 * The email-gate envelope fields the EmailPreview editor already owns —
 * to/cc/bcc/subject/body seeded by the backend's `build_email_gate_payload`,
 * plus the two frontend-only fields this page adds itself (attachments,
 * sendFromConnectionId). Any OTHER GateField on an email gate's `gateSchema`
 * is an extra, step-appended field (e.g. the speaker confirmation gate's
 * `hotelBlock` select) and is rendered generically via FormRenderer.
 */
const EMAIL_ENVELOPE_KEYS = new Set([
  'to',
  'cc',
  'bcc',
  'subject',
  'body',
  'attachments',
  'removedPinnedAttachments',
  'sendFromConnectionId',
]);

export function normalizeGateSchema(raw: unknown): JSONSchema {
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'properties' in raw) {
    return raw as JSONSchema;
  }
  if (!Array.isArray(raw)) {
    return { properties: {} };
  }
  const fields = raw as GateField[];
  const properties: Record<
    string,
    { type: string; title?: string; format?: string; enum?: readonly string[] }
  > = {};
  const readonly: string[] = [];
  for (const f of fields) {
    if (!f || typeof f !== 'object' || typeof f.key !== 'string') continue;
    let prop: { type: string; title?: string; format?: string; enum?: readonly string[] };
    switch (f.type) {
      case 'number':
        prop = { type: 'number', title: f.label };
        break;
      case 'textarea':
        prop = { type: 'string', title: f.label, format: 'textarea' };
        break;
      case 'date':
        prop = { type: 'string', title: f.label, format: 'date' };
        break;
      case 'email':
        prop = { type: 'string', title: f.label, format: 'email' };
        break;
      case 'boolean':
        prop = { type: 'boolean', title: f.label };
        break;
      case 'select':
        // enum drives FormRenderer's <select>. Drop options on a non-array so a
        // malformed field degrades to a plain text input rather than crashing.
        prop = {
          type: 'string',
          title: f.label,
          ...(Array.isArray(f.options) ? { enum: f.options } : {}),
        };
        break;
      case 'readonly':
      case 'string':
      default:
        prop = { type: 'string', title: f.label };
        break;
    }
    properties[f.key] = prop;
    if (f.editable === false || f.type === 'readonly') readonly.push(f.key);
  }
  return { properties, readonly } as JSONSchema;
}

/**
 * D3 speaker inserts: the confirmation gate annotates gateData with a
 * `registrationCheck` blob (email_gates.py) telling the reviewer whether the
 * registration ask was kept or dropped. One chip per state.
 */
const REG_CHIP: Record<
  string,
  { cls: string; icon: React.ComponentProps<typeof Icon>['name']; text: string }
> = {
  registered: {
    cls: 'border-completed-border bg-completed-soft text-completed',
    icon: 'check',
    text: 'Already registered — registration ask removed',
  },
  not_registered: {
    cls: 'border-accent-border bg-accent-soft text-accent-text',
    icon: 'info',
    text: 'Not yet registered — registration ask included',
  },
  no_links: {
    cls: 'border-border bg-bg-muted text-text-subtle',
    icon: 'info',
    text: 'No registration links configured for this event',
  },
  unverified: {
    cls: 'border-waiting-border bg-waiting-soft text-waiting',
    icon: 'alert',
    text: "Couldn't verify registration — ask kept",
  },
};

/**
 * GateReview page (UI-06). Renders form-driven (data-gate) or email-driven
 * (email-gate) review primitive based on the discriminated union in
 * gateData. The FormProvider wraps the whole render tree so FormRenderer /
 * EmailPreview can call useFormContext directly.
 *
 * @sideEffects subscribes to ['workflow', id]; mutates on Approve/Reject/Resubmit;
 *              writes drafts to localStorage; publishes topbar content
 * @idempotent yes — multiple mounts share React Query + localStorage state
 */
export function GateReview() {
  const params = useParams<{
    id?: string;
    workflowId?: string;
    step?: string;
    stepIndex?: string;
  }>();
  // Route accepts EITHER /workflows/:id/gate/:step OR /approvals/:workflowId/:stepIndex.
  const workflowId = params.workflowId ?? params.id;
  const stepIndexStr = params.stepIndex ?? params.step;
  const stepIndex = stepIndexStr ? parseInt(stepIndexStr, 10) : NaN;

  const navigate = useNavigate();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { data, isPending, error } = useWorkflow(workflowId);
  // Where "done here" lands: the speaker hub for speaker gates (the journey
  // view IS the speaker workflow view), the step-list detail for the rest.
  const workflowLanding = data?.workflow ? workflowPath(data.workflow) : `/workflows/${workflowId}`;
  const approve = useApproveGate();
  const reject = useRejectGate();
  const resubmit = useResubmitGate();
  const skip = useSkipGate();
  const regenerate = useRegenerateGate();
  const revertTracker = useRevertTracker();

  // Wave 2 ops automation runs have their own review page (editable rows table).
  useEffect(() => {
    if (data?.workflow.type.startsWith('ops_'))
      navigate(`/ops/runs/${data.workflow.id}`, { replace: true });
  }, [data?.workflow.type, data?.workflow.id, navigate]);
  const step: StepExecution | undefined = useMemo(
    () => data?.steps.find((s) => s.stepIndex === stepIndex),
    [data, stepIndex],
  );

  const gateData: GateData | null = useMemo(() => {
    const raw = step?.gateData;
    if (!raw || typeof raw !== 'object') return null;
    const kind = (raw as { kind?: unknown }).kind;
    if (kind === 'data-gate' || kind === 'email-gate') {
      return raw as GateData;
    }
    return null;
  }, [step?.gateData]);

  // Sent view (2026-07-14): a completed/skipped step renders read-only with
  // the follow-up plan editor instead of a stale gate form (which 409'd on
  // submit). The tracked-email fetch only fires once the step is done.
  const stepDone = step?.status === 'completed' || step?.status === 'skipped';
  const updateNudges = useUpdateNudgePlan();
  const sendNudge = useSendNudgeNow();
  const trackedEmails = useTrackedEmails(workflowId ? { workflowId } : {}, {
    enabled: stepDone && workflowId != null,
  });

  const isEmailGate = gateData?.kind === 'email-gate';
  const isDataGate = gateData?.kind === 'data-gate';
  const isRejected = step?.status === 'rejected';
  const isResubmit =
    isRejected ||
    (gateData != null && gateData.lastEdits != null && gateData.rejectionReason != null);

  // Multi-account Gmail (2026-06): the reviewer picks WHICH connected account
  // the send goes from. The dropdown's initial value mirrors the backend
  // resolver in send_as_approver — per-send override → per-event default →
  // first-connected healthy account — so what the reviewer sees pre-selected
  // is exactly what the backend would do if they never touched it. The pick
  // rides the approval edits as `sendFromConnectionId`.
  const healthyConnections = useMemo(
    () => (me?.gmailConnections ?? []).filter((c) => c.lastErrorReason == null),
    [me?.gmailConnections],
  );
  const eventId = data?.workflow.eventId;
  const defaultSendFromId = useMemo(() => {
    if (healthyConnections.length === 0) return '';
    const mapped = eventId ? me?.gmailEventDefaults?.[eventId] : undefined;
    return mapped && healthyConnections.some((c) => c.id === mapped)
      ? mapped
      : (healthyConnections[0]?.id ?? '');
  }, [healthyConnections, eventId, me?.gmailEventDefaults]);

  // Invite gate only: the reviewer decides "reply on an existing thread" AT the
  // draft (design §5, moved off kickoff). The picker lists the reviewer's own
  // recent Gmail threads with the speaker; the pick rides the approve edits as
  // `replyThread` and the approval resolves + threads it server-side.
  const isInviteGate =
    isEmailGate && step?.stepId === 'invite' && data?.workflow.type === 'speaker_onboarding';
  const inviteSpeakerEmail =
    isInviteGate && gateData?.kind === 'email-gate'
      ? (gateData.email.to.split(',')[0]?.trim() ?? null)
      : null;
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadPick, setThreadPick] = useState<string | null>(null);
  const inviteThreads = useInviteThreads(
    eventId,
    inviteSpeakerEmail,
    Boolean(isInviteGate) && threadOpen,
  );

  // The server preserves the literal `[Your Name]` in the rendered body so it
  // can be substituted with whoever actually approves. For the *preview*, the
  // logged-in user IS that reviewer, so swap their name in up-front (idempotent
  // with the server's onApproval replace). `substituteSenderName` mirrors the
  // backend exactly — FIRST name only, EVERY occurrence (intro + sign-off).
  // Hoisted so both the initial defaults and the regenerate reseed share it.
  const substituteApproverName = useCallback(
    (body: string): string => substituteSenderName(body, me?.name),
    [me?.name],
  );

  // Email-gate context (copy rationale + the ops templatePicker) lifted into
  // local state. A regenerate refreshes the rationale + picker in place from
  // its RESPONSE rather than a workflow refetch — a refetch would re-run the
  // draft-reset effect and clobber the just-reseeded body (F14). Re-seeded
  // whenever the server context genuinely changes (background WS refetch).
  const serverContext = gateData?.kind === 'email-gate' ? gateData.context : undefined;
  const serverContextKey = useMemo(() => JSON.stringify(serverContext ?? null), [serverContext]);
  const [liveContext, setLiveContext] = useState<Record<string, unknown> | undefined>(
    serverContext,
  );
  useEffect(() => {
    setLiveContext(serverContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverContextKey]);

  // Extra (non-envelope) fields on an EMAIL gate — e.g. the speaker
  // confirmation gate's hotelBlock select. The engine persists every gate's
  // GateField defs on step.gateSchema; the email-gate branch historically
  // rendered only the EmailPreview, so step-appended fields never showed.
  // Filter the envelope out and render the rest with the same FormRenderer the
  // data-gate branch uses — they register into the same RHF form, so their
  // edited values ride the approve payload alongside the email fields.
  const extraEmailFields = useMemo<GateField[]>(() => {
    if (!isEmailGate) return [];
    const raw: unknown = step?.gateSchema;
    if (!Array.isArray(raw)) return [];
    return (raw as unknown[]).filter(
      (f): f is GateField =>
        f != null &&
        typeof f === 'object' &&
        typeof (f as { key?: unknown }).key === 'string' &&
        !EMAIL_ENVELOPE_KEYS.has((f as { key: string }).key),
    );
  }, [isEmailGate, step?.gateSchema]);

  // Speaker card gate only (2026-07-13 flow split — the picker moved off the
  // promotion email onto its own data-gate): the candidate manifest for the
  // QC panel (null on every other gate, or when the blob is malformed). The
  // data-gate bundling nests the flat gateData under `defaults`.
  const cardCandidates = useMemo(
    () =>
      isDataGate && step?.stepId === 'card_assets' && gateData?.kind === 'data-gate'
        ? readCardCandidates(gateData.defaults)
        : null,
    [isDataGate, step?.stepId, gateData],
  );

  // Card gate only: the two-phase state (draft grid vs. finalized preview) —
  // defensive reader, never null when the manifest parsed (draft floor).
  const cardGate = useMemo(
    () =>
      isDataGate && step?.stepId === 'card_assets' && gateData?.kind === 'data-gate'
        ? readCardGateState(gateData.defaults)
        : null,
    [isDataGate, step?.stepId, gateData],
  );

  // Fields the generic FormRenderer shows. When the card panel is mounted it
  // OWNS `chosenCandidate` + `highlightColor` (duplicate selects would be
  // confusing), so those fields are hidden here — but they stay in
  // extraEmailFields so the defaultValues seeding below still picks up the
  // server's defaults and the values ride the approve edits either way.
  const visibleExtraEmailFields = useMemo<GateField[]>(
    () =>
      cardCandidates != null
        ? extraEmailFields.filter((f) => f.key !== 'chosenCandidate' && f.key !== 'highlightColor')
        : extraEmailFields,
    [extraEmailFields, cardCandidates],
  );

  // Derive default values: data-gate uses defaults from schema; email-gate
  // uses email object. Resubmit overlays lastEdits on top (D-4.4).
  const defaultValues = useMemo<Record<string, unknown>>(() => {
    if (isEmailGate && gateData && gateData.kind === 'email-gate') {
      const base: Record<string, unknown> = {
        to: gateData.email.to,
        cc: gateData.email.cc ?? '',
        bcc: gateData.email.bcc ?? '',
        subject: gateData.email.subject,
        body: substituteApproverName(gateData.email.body),
        // '' while me / connections are still loading — the reset effect
        // re-fires once defaultSendFromId resolves (defaultsKey changes).
        sendFromConnectionId: defaultSendFromId,
        // Reviewer-uploaded files (EmailPreview AttachmentPicker). Never
        // seeded by the server and excluded from the localStorage draft
        // (base64 payloads would blow the quota), so always start empty.
        attachments: [],
        // Pinned server files the reviewer took off (sponsor welcome logos).
        removedPinnedAttachments: [],
        // Invite gate only: the "reply on an existing thread" pick made at the
        // draft. null = a fresh email; {connectionId, threadId} = reply on that
        // thread (the approval resolves it server-side). Rides the approve edits.
        replyThread: null,
      };
      // Extra step-appended fields: seed each from its top-level gateData
      // value (build_email_gate_payload merges extra_data into the gate's
      // data dict, e.g. `hotelBlock` = the first configured block's label).
      for (const f of extraEmailFields) {
        base[f.key] = (gateData as unknown as Record<string, unknown>)[f.key] ?? '';
      }
      return isResubmit && gateData.lastEdits ? { ...base, ...gateData.lastEdits } : base;
    }
    if (isDataGate && gateData && gateData.kind === 'data-gate') {
      const base: Record<string, unknown> = { ...(gateData.defaults ?? {}) };
      // The card gate's candidate manifest + phase bookkeeping are display
      // data, not form values — keep them out of RHF so they don't ride the
      // approve edits back up. (chosenCandidate + highlightColor STAY: the
      // panel binds them as real form fields.)
      delete base.cardCandidates;
      delete base.cardPhase;
      delete base.highlightColors;
      delete base.finalPreview;
      return isResubmit && gateData.lastEdits ? { ...base, ...gateData.lastEdits } : base;
    }
    return {};
  }, [
    gateData,
    isEmailGate,
    isDataGate,
    isResubmit,
    substituteApproverName,
    defaultSendFromId,
    extraEmailFields,
  ]);

  const form = useForm<Record<string, unknown>>({
    defaultValues,
    mode: 'onSubmit',
  });

  // Reflect the invite thread pick into the RHF `replyThread` value so it rides
  // the approve edits. A blank pick ("New email") clears it → a fresh send.
  const onPickThread = useCallback(
    (pick: string | null) => {
      setThreadPick(pick);
      const [connectionId, threadId] = (pick ?? '').split(' ');
      form.setValue(
        'replyThread',
        pick && connectionId && threadId ? { connectionId, threadId } : null,
      );
    },
    [form],
  );

  // Stable string key so a fresh object identity each render doesn't re-loop
  // the reset effect. Passed into useGateDraft, which owns the single reset
  // (a live draft wins over server defaults — F14).
  const defaultsKey = useMemo(() => JSON.stringify(defaultValues), [defaultValues]);

  const draft = useGateDraft(workflowId, stepIndex, me?.id, form, defaultValues, defaultsKey);

  // Live-watch the picked sending connection so the From row tracks the
  // dropdown. Falls back to a placeholder when the reviewer has no healthy
  // connection so the row never shows literal `undefined`.
  const watchedSendFrom = form.watch('sendFromConnectionId');
  const fromAddress =
    (me?.gmailConnections ?? []).find((c) => c.id === watchedSendFrom)?.email ??
    healthyConnections[0]?.email ??
    '(connect Gmail to send)';

  // Ops gate only: live-load the welcome→ops conversation so the reviewer sees
  // the latest thread context before sending. Read from the mailbox they'll send
  // as (the selected send-from), which was CC'd on the welcome and holds it all.
  const isOpsGate = step?.stepId === 'ops_next_steps';
  const watchedSendFromId = typeof watchedSendFrom === 'string' ? watchedSendFrom : undefined;
  const opsThread = useOpsEmailThread(workflowId, watchedSendFromId, isEmailGate && isOpsGate);

  useSetTopbar({
    breadcrumbs: data?.workflow
      ? [
          // Speaker gates crumb home to Speakers, sponsor gates to Sponsors
          // (no generic Workflows tab since the 2026-07 nav restructure).
          workflowHomeCrumb(data.workflow.type),
          {
            label: data.workflow.displayName,
            to: workflowPath(data.workflow),
          },
          {
            label: `Gate review · Step ${Number.isNaN(stepIndex) ? '?' : stepIndex + 1}`,
          },
        ]
      : [{ label: 'Approvals', to: '/approvals' }, { label: 'Loading…' }],
    actions: (
      <Button size="sm" variant="ghost" onClick={() => navigate(-1)}>
        <Icon name="arrow-left" size={14} />
        Back
      </Button>
    ),
  });

  const [showReject, setShowReject] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  // Speaker email gates: "Regenerate" = fresh redraft from the event's current
  // templates/defaults (drops this draft's edits; nothing sent). Confirm first.
  const [regenTargets, setRegenTargets] = useState<RegenerateTarget[] | null>(null);

  if (isPending) {
    return (
      <div className="max-w-[880px] mx-auto flex flex-col gap-4">
        <div role="status" className="text-xs text-text-subtle">
          Loading this step — the workflow, its state, and the draft under review…
        </div>
        <Skeleton variant="row" height={80} />
        <Skeleton variant="card" />
        <Skeleton variant="row" height={48} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Empty
        icon={<Icon name="alert" size={20} />}
        title="Couldn't load gate"
        description={
          error instanceof Error ? error.message : 'The gate may already have been actioned.'
        }
        action={
          <Button variant="primary" onClick={() => navigate('/approvals')}>
            Back to approvals
          </Button>
        }
      />
    );
  }

  if (!step || Number.isNaN(stepIndex)) {
    return (
      <Empty
        icon={<Icon name="alert" size={20} />}
        title="Step not found"
        description={`Step ${stepIndexStr ?? '(missing)'} doesn't exist on this workflow.`}
        action={
          <Button variant="primary" onClick={() => navigate('/approvals')}>
            Back to approvals
          </Button>
        }
      />
    );
  }

  // ------------------------------------------------------------------
  // SENT view — the step already ran. No form, no 409s: a done banner,
  // and when this step sent a tracked email that carries a follow-up
  // plan, the D1 plan editor right here on the step's page.
  // ------------------------------------------------------------------
  if (stepDone) {
    const te = (trackedEmails.data ?? []).find((t) => t.stepId === step.stepId) ?? null;
    const hasPlan = te != null && (te.nudgeTemplate != null || (te.nudgePlan ?? []).length > 0);
    // The chase is over once the paired receipt step finished (or the whole
    // workflow ended) — mirrors RECEIPT_STEP_BY_SEND_STEP on the server.
    const receiptId =
      step.stepId === 'invite'
        ? 'invite_reply'
        : step.stepId === 'confirmation'
          ? 'asset_collection'
          : null;
    const receiptStep = receiptId ? data.steps.find((s) => s.stepId === receiptId) : undefined;
    const chaseOver =
      (receiptStep != null &&
        (receiptStep.status === 'completed' || receiptStep.status === 'skipped')) ||
      ['completed', 'failed', 'cancelled'].includes(data.workflow.status);
    const wasSkipped = step.status === 'skipped';
    const doneAt = step.completedAt
      ? new Date(step.completedAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : null;
    const wasEmail = te != null || isEmailGate;
    return (
      <div className="max-w-[880px] mx-auto flex flex-col gap-4 pb-8">
        <div>
          <h2 className="text-[15px] font-semibold -tracking-[0.01em] text-text">
            {titleCase(step.stepId)}
          </h2>
          <p className="text-[13px] text-text-muted mt-1">
            Step {stepIndex + 1} · already complete — nothing to review here.
          </p>
        </div>

        {/* Done banner */}
        <div className="flex items-start gap-2.5 rounded-lg border border-completed-border bg-completed-soft px-4 py-3 text-completed">
          <Icon name="check" size={16} className="mt-0.5 shrink-0" />
          <div className="text-[13px]">
            <div className="font-semibold mb-0.5">
              {wasSkipped
                ? wasEmail
                  ? 'Marked sent outside Pixel'
                  : 'Step skipped'
                : wasEmail
                  ? 'Email sent'
                  : 'Step complete'}
            </div>
            {te ? (
              <span>
                <strong className="font-semibold">{te.subject}</strong> to {te.recipientEmail}
              </span>
            ) : null}
            {(step.approvedByName || doneAt) && (
              <span>
                {te ? ' · ' : ''}
                {step.approvedByName ? `by ${step.approvedByName}` : ''}
                {step.approvedByName && doneAt ? ' · ' : ''}
                {doneAt ?? ''}
              </span>
            )}
          </div>
        </div>

        {/* The follow-up plan editor, right where the email was sent (D1). */}
        {te && hasPlan && (
          <NudgeTimeline
            summary={{
              trackedEmailId: te.id,
              stepId: te.stepId,
              subject: te.subject,
              status: te.status,
              plan: te.nudgePlan ?? [],
            }}
            recipientName={te.recipientName ?? te.recipientEmail}
            recipientEmail={te.recipientEmail}
            fromEmail={te.senderEmail}
            chaseOver={chaseOver}
            update={updateNudges}
            sendNow={sendNudge}
          />
        )}
        {trackedEmails.isPending && stepDone && workflowId != null && (
          <Skeleton variant="row" height={64} />
        )}

        <div>
          <Button type="button" variant="default" onClick={() => navigate(workflowLanding)}>
            <Icon name="arrow-left" size={13} />
            Back to {data.workflow.type === 'speaker_onboarding' ? 'the speaker' : 'the workflow'}
          </Button>
        </div>
      </div>
    );
  }

  const stepName = titleCase(step.stepId);
  // 2026-06-09 product decision: sponsor onboarding gates are not role-gated —
  // any authenticated user may approve any gate (mirrors the backend no-op in
  // runner.py `_check_gate_role`). `step.gateRole` is still shown as a badge for
  // context, but it no longer restricts approval.
  const canApprove = me != null;

  // D3 speaker inserts: speaker gates use the SQUARE-BRACKET placeholder
  // convention ([TBD], [Registration link], …); the server's approve guard
  // 409s on any of them — surface the count LIVE so reviewers never trip the
  // backstop. Sponsor gates keep their own `{...}` convention untouched.
  const isSpeakerGate = data.workflow.type === 'speaker_onboarding';
  const speakerSubject = String(form.watch('subject') ?? '');
  const speakerBody = String(form.watch('body') ?? '');
  const speakerPlaceholders =
    isSpeakerGate && isEmailGate ? uniqueSpeakerPlaceholders(speakerSubject, speakerBody) : [];
  // Card gate: the server refuses an approval until the speaker card has been
  // built and looked at (card_gate._staged_card_file_id) — mirror that here so
  // the reviewer gets a labelled, disabled button instead of a 409. A "skip"
  // pick publishes nothing, so it's exempt; so is a card already on the board.
  const cardPick = String(form.watch('chosenCandidate') ?? '');
  const cardBuildRequired =
    cardGate != null &&
    cardGate.phase !== 'card' &&
    cardCandidates?.reuseStage !== 'card' &&
    cardPick !== '' &&
    cardPick !== 'skip';

  // Registration annotation set by the confirmation gate's compose (see
  // REG_CHIP above). extra_data merges into gateData top-level, so read it
  // off the widened record like the extra email fields do.
  const registrationState =
    isSpeakerGate && isEmailGate && gateData
      ? ((
          (gateData as unknown as Record<string, unknown>).registrationCheck as
            | { state?: string }
            | undefined
        )?.state ?? null)
      : null;
  const regChip = registrationState ? REG_CHIP[registrationState] : undefined;
  // PoE prefill status set by the invite gate's compose (extra_data merges into
  // gateData top-level). Present only on the invite gate; drives the
  // "session details not pre-filled" banner.
  const poeStatus =
    isSpeakerGate && isEmailGate && gateData
      ? (((gateData as unknown as Record<string, unknown>).poe as
          | { configured?: boolean; matched?: boolean; speakerName?: string }
          | undefined) ?? null)
      : null;
  // Multi-session flag (logistics gate): the day-of email shows one session, so
  // warn when the speaker is on 2+ agenda sessions and list them.
  const multiSession =
    isSpeakerGate && isEmailGate && gateData
      ? (((gateData as unknown as Record<string, unknown>).multiSession as
          | {
              count?: number;
              sessions?: { title?: string; day?: string; time?: string; stage?: string }[];
            }
          | undefined) ?? null)
      : null;

  /**
   * Land the reviewer on the first unfilled placeholder. The subject is a
   * plain RHF input (selectable); the body is a contenteditable rich-text
   * editor, so walk its text nodes and select the token via a DOM Range.
   */
  const jumpToFirstPlaceholder = () => {
    const [inSubject] = findSpeakerPlaceholders(speakerSubject);
    if (inSubject) {
      const el = document.getElementById('email-subject') as HTMLInputElement | null;
      if (el) {
        el.focus();
        el.setSelectionRange(inSubject.index, inSubject.index + inSubject.token.length);
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return;
    }
    const editor = document.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editor) return;
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const text = node.textContent ?? '';
      const [hit] = findSpeakerPlaceholders(text);
      if (!hit) continue;
      const range = document.createRange();
      range.setStart(node, hit.index);
      range.setEnd(node, Math.min(text.length, hit.index + hit.token.length));
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      editor.focus();
      (node.parentElement ?? editor).scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    // Token split across formatting nodes — at least land on the editor.
    editor.focus();
    editor.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // Nudge-seeding speaker email gates (invite / confirmation): approving
  // STAYS on this page, which re-renders as the sent view with the freshly
  // seeded follow-up plan editor — "send at the step, then set up the nudge
  // schedule immediately" (2026-07-14, Danny).
  const staysForPlan =
    isSpeakerGate && isEmailGate && (step.stepId === 'invite' || step.stepId === 'confirmation');

  const onApprove = (values: Record<string, unknown>) => {
    if (!workflowId) return;
    approve.mutate(
      {
        workflowId,
        stepIndex,
        edits: values,
        successMessage: isEmailGate
          ? 'Email sent — workflow resumed'
          : 'Approved — workflow resumed',
      },
      {
        onSuccess: () => {
          draft.clear();
          if (staysForPlan) {
            // The approve hook's onSettled already refetches the workflow;
            // this pulls the just-created TrackedEmail (with its plan) too.
            void queryClient.invalidateQueries({ queryKey: qk.trackedEmails.root() });
          } else {
            navigate(workflowLanding);
          }
        },
        onError: (err) => {
          if (err instanceof ApiClientError && err.code === 'VALIDATION_ERROR') {
            const fieldErrors =
              (err.details as { fieldErrors?: Record<string, string[]> })?.fieldErrors ?? {};
            for (const [name, msgs] of Object.entries(fieldErrors)) {
              if (msgs && msgs.length > 0) {
                form.setError(name, { message: msgs[0] });
              }
            }
          }
          // Toast already emitted by the mutation hook's onError.
        },
      },
    );
  };

  const onReject = (reason: string) => {
    if (!workflowId) return;
    reject.mutate(
      { workflowId, stepIndex, reason },
      {
        onSuccess: () => {
          // F12: keep the draft on reject for EMAIL gates. The backend folds
          // reject edits into `lastEdits` only for DATA gates (email gates
          // return early in `_frontend_gate_data`), so the per-user
          // localStorage draft is the ONLY thing preserving the reviewer's
          // hand-edited body to resubmit. Clearing it here silently reverted
          // the editor to the stock composed body while the banner claimed it
          // was pre-filled. Only clear for non-email gates.
          if (!isEmailGate) draft.clear();
          setShowReject(false);
          navigate(workflowLanding);
        },
      },
    );
  };

  const onResubmit = () => {
    if (!workflowId) return;
    resubmit.mutate({ workflowId, stepIndex });
  };

  const onSkip = () => {
    if (!workflowId) return;
    skip.mutate(
      {
        workflowId,
        stepIndex,
        edits: form.getValues(),
        successMessage: 'Skipped — workflow resumed',
      },
      {
        onSuccess: () => {
          draft.clear();
          setShowSkipConfirm(false);
          navigate(workflowLanding);
        },
      },
    );
  };

  // Ops gate only: recompose the email body from the reviewer's section picks.
  // The gate stays paused; we reseed the editor from the RESPONSE (subject +
  // body + recipients) and refresh the rationale/picker via liveContext. The
  // mutation deliberately doesn't invalidate the workflow query, so the
  // draft-reset effect won't fire and discard this reseed (F14).
  const onRegenerate = (selectedSections: string[]) => {
    if (!workflowId) return;
    regenerate.mutate(
      { workflowId, stepIndex, selected: selectedSections },
      {
        onSuccess: (res) => {
          // Regenerate re-picks an ops section to refresh the BODY only.
          // Preserve the reviewer's curated to/cc/bcc/subject — re-picking a
          // section must not clobber a deliberately-edited envelope (incl. a
          // removed sales-ops BCC or an added contact) — audit F31.
          if (res.email) {
            form.setValue('body', substituteApproverName(res.email.body), {
              shouldDirty: true,
            });
          }
          setLiveContext(res.context);
        },
      },
    );
  };

  // Promotion gate only: re-run speaker-card candidate generation. Unlike the
  // ops reseed-in-place above, the fresh manifest lives only on the
  // re-persisted gateData (the regenerate response carries just
  // {email, context}), so the workflow query is refetched to re-render the
  // panel. Safe against F14: nothing is reseeded into the form here, the
  // server keeps the persisted email envelope, and the reviewer's live edits
  // win per-field over refreshed defaults via the localStorage draft.
  const onRegenerateCards = () => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        successMessage: 'Fresh card candidates generated',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  // Card gate escape hatch: paint a FRESH illustration, ignoring whatever
  // artwork already sits on the speaker's monday row. Ordinary regeneration
  // (above) reuses the board so a reviewer can't accidentally spend a
  // generation on a speaker the design team already finished; this is the
  // deliberate "no, redo it" — a distinct server action (card_gate
  // .ACTION_FORCE_REGENERATE), not a flag on the plain one.
  const onForceRegenerateCards = () => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        action: 'force_regenerate',
        successMessage: 'Fresh illustration generated',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  // Card gate step 2: paint the chosen draft with the highlight halo and
  // persist the finalized preview (gate flips to phase "final"). Same
  // refetch-to-re-render pattern as onRegenerateCards — the new phase lives
  // on the re-persisted gateData.
  const onFinalizeCard = (chosen: string, color: string) => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        action: 'finalize',
        chosenCandidate: chosen,
        highlightColor: color,
        successMessage: 'Final portrait created',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  // Card gate step 3: compose the speaker card and stage it in Drive so the
  // reviewer SEES it before approving (gate flips to phase "card"). Approval
  // republishes those exact bytes — see card_gate._build_card_gate.
  const onBuildCard = () => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        action: 'build_card',
        successMessage: 'Speaker card built',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  // Card gate: step back from the staged card to the finished portrait (keeps
  // the portrait; drops the staged card server-side).
  const onBackToFinalPortrait = () => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        action: 'back_to_final',
        successMessage: 'Back to the portrait',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  // Card gate: return from the finalized preview to the draft grid (keeps the
  // existing drafts; drops the preview reference server-side).
  const onBackToDrafts = () => {
    if (!workflowId) return;
    regenerate.mutate(
      {
        workflowId,
        stepIndex,
        selected: [],
        action: 'back',
        successMessage: 'Back to drafts',
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: qk.workflow.root(workflowId) });
        },
      },
    );
  };

  const submit = form.handleSubmit((values) => {
    // Speaker email gates: block submit while square-bracket placeholders
    // remain (the server would 409 anyway) — jump the reviewer to the first.
    if (speakerPlaceholders.length > 0) {
      jumpToFirstPlaceholder();
      return;
    }
    if (isEmailGate) {
      setShowApproveConfirm(true);
    } else {
      onApprove(values);
    }
  });

  const priorRejectionReason =
    step.rejectionReason ??
    (gateData && 'rejectionReason' in gateData ? gateData.rejectionReason : undefined) ??
    'No reason provided';

  return (
    <FormProvider {...form}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'Enter') {
            e.preventDefault();
            setShowReject(true);
          } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void submit();
          } else if (e.key === 'Escape') {
            setShowReject(false);
            setShowApproveConfirm(false);
            setShowSkipConfirm(false);
          }
        }}
        className="max-w-[880px] mx-auto pb-32 sm:pb-24"
      >
        {isResubmit && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-waiting-soft border border-waiting-border text-waiting"
          >
            <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
            <div className="text-[13px]">
              <div className="font-semibold mb-0.5">Resubmitting after rejection</div>
              Rejected
              {step.approvedByName ? ` by ${step.approvedByName}` : ''}: "{priorRejectionReason}".{' '}
              {/* F12: only claim a pre-fill when one actually happened. Email
                  gates rely on the surviving localStorage draft (the backend
                  doesn't fold reject edits into email-gate lastEdits); data
                  gates always carry lastEdits from the server. */}
              {isEmailGate && !draft.hasDraft
                ? 'Composed from the original template — make corrections and resubmit.'
                : 'Form pre-filled with your previous edits — make corrections and resubmit.'}
            </div>
          </div>
        )}

        {/* Invite gate only (the poe block is set by build_invite_gate): explain
            WHY the draft shows [TBD]s — no PoE sheet configured, or the speaker's
            name isn't on it — instead of leaving the reviewer to guess. */}
        {poeStatus && !poeStatus.matched && (
          <div
            role="status"
            className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-waiting-soft border border-waiting-border text-waiting"
          >
            <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
            <div className="text-[13px]">
              <div className="font-semibold mb-0.5">Session details not pre-filled</div>
              {poeStatus.configured
                ? `"${poeStatus.speakerName}" wasn't found in the Program of Events sheet, so the session details below are [TBD]. Fill them in by hand before sending — and add the speaker to the PoE sheet so the confirmation email pre-fills.`
                : 'This event has no Program of Events sheet configured, so session details are [TBD] — fill them in by hand before sending.'}
            </div>
          </div>
        )}

        {/* Multi-session flag: this speaker is on more than one agenda session,
            but the logistics email shows only one — the reviewer must add/handle
            the others before sending. */}
        {multiSession && (multiSession.count ?? 0) > 1 && (
          <div
            role="status"
            className="mb-5 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-waiting-soft border border-waiting-border text-waiting"
          >
            <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
            <div className="text-[13px]">
              <div className="font-semibold mb-0.5">
                On {multiSession.count} agenda sessions — this email shows one
              </div>
              This speaker has multiple sessions; the draft below covers a single one. Review and
              add the others before sending:
              <ul className="mt-1.5 list-disc pl-5">
                {(multiSession.sessions ?? []).map((s, i) => (
                  <li key={i}>
                    <span className="font-medium">{s.title || 'Untitled session'}</span>
                    {[s.day, s.time, s.stage].filter(Boolean).length > 0 && (
                      <span className="text-text-muted">
                        {' — '}
                        {[s.day, s.time, s.stage].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="mb-5">
          <h2 className="text-[15px] font-semibold -tracking-[0.01em] text-text">{stepName}</h2>
          <p className="text-[13px] text-text-muted mt-1 flex items-center gap-2 flex-wrap">
            {step.gateRole && <Badge variant="role">{step.gateRole.replace('_', ' ')}</Badge>}
            <span>Awaiting your approval. The workflow is paused at step {stepIndex + 1}.</span>
          </p>
        </div>

        {isEmailGate && gateData && gateData.kind === 'email-gate' ? (
          <>
            {/* D3: registration chip (confirmation gate only) — was the
                registration ask kept or dropped, and why. */}
            {regChip && (
              <div
                className={`mb-4 inline-flex items-center gap-2 rounded-full border py-[5px] pl-2.5 pr-3 text-[12px] font-semibold ${regChip.cls}`}
              >
                <Icon name={regChip.icon} size={13} />
                {regChip.text}
              </div>
            )}
            {isOpsGate && (
              <EmailThreadViewer
                thread={opsThread.data}
                isLoading={opsThread.isLoading}
                className="mb-4"
              />
            )}
            {(() => {
              const picker = readTemplatePicker(liveContext);
              if (!picker) return null;
              // Remount (reset local picks) only once a regenerate composes a
              // new selection — a stable key keeps in-progress toggles while
              // the reviewer edits, then resets to the applied picks on apply.
              const pickerKey = [...picker.selected].sort().join(' ');
              return (
                <OpsTemplatePicker
                  key={pickerKey}
                  picker={picker}
                  disabled={!canApprove}
                  isRegenerating={regenerate.isPending}
                  onRegenerate={onRegenerate}
                  className="mb-4"
                />
              );
            })()}
            {(() => {
              const rationale = readCopyRationale(liveContext);
              return rationale ? (
                <CopyRationaleCard rationale={rationale} className="mb-4" />
              ) : null;
            })()}
            {/* Speaker promotion gate: the board card is server-attached on
                send — when present it shows as a pinned chip in the preview's
                attachment list (published by the Generate Illustrations step).
                Approval re-reads the board and BLOCKS when the card is
                missing, so surface missing/error before the reviewer hits
                Approve. */}
            {step.stepId === 'promotion' &&
              (() => {
                const status = readMondayCard(gateData)?.status;
                if (status === 'missing') {
                  return (
                    <div className="mb-4 flex items-center gap-2 rounded-md border border-waiting-border bg-waiting-soft px-3.5 py-2.5 text-[12.5px] font-medium text-waiting">
                      <Icon name="alert" size={13} />
                      No speaker card on the monday board yet — complete the Generate Illustrations
                      step first. Approval is blocked until the card exists.
                    </div>
                  );
                }
                if (status === 'error') {
                  return (
                    <div className="mb-4 flex items-center gap-2 rounded-md border border-waiting-border bg-waiting-soft px-3.5 py-2.5 text-[12.5px] font-medium text-waiting">
                      <Icon name="alert" size={13} />
                      Couldn&apos;t check the monday board for the speaker card — approval will
                      retry the read.
                    </div>
                  );
                }
                return null;
              })()}
            {/* Extra step-appended (non-envelope) fields — e.g. the speaker
                confirmation gate's hotelBlock select. Same FormRenderer as the
                data-gate branch; values land in the approve edits with the
                email fields. */}
            {visibleExtraEmailFields.length > 0 && (
              <FormRenderer
                schema={normalizeGateSchema(visibleExtraEmailFields)}
                className="mb-4"
              />
            )}
            {/* Invite gate only: reply-on-an-existing-thread, chosen at the draft.
                Reviewer picks one of THEIR threads with the speaker; the invite
                then sends as a reply on it (In-Reply-To), else a fresh email. */}
            {isInviteGate && (
              <InviteThreadPicker
                open={threadOpen}
                onToggle={() => setThreadOpen((v) => !v)}
                speakerEmail={inviteSpeakerEmail}
                query={inviteThreads}
                selected={threadPick}
                onSelect={onPickThread}
                className="mb-4"
              />
            )}
            <EmailPreview
              from={fromAddress}
              pinnedAttachments={
                step.stepId === 'promotion'
                  ? promotionCardPinned(workflowId, gateData)
                  : gatePinnedAttachments(gateData, workflowId, step.stepIndex)
              }
              fromSelector={
                healthyConnections.length > 1 ? (
                  <Select
                    id="email-from"
                    aria-label="Send from"
                    {...form.register('sendFromConnectionId')}
                    className="w-full"
                  >
                    {healthyConnections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.email}
                      </option>
                    ))}
                  </Select>
                ) : undefined
              }
            />
          </>
        ) : isDataGate && gateData && gateData.kind === 'data-gate' ? (
          <>
            {(() => {
              const tc = gateData.trackerChanges;
              if (!tc) return null;
              // Live revert state wins over the static gateData snapshot —
              // the blob never updates after the gate opens, but the revert
              // endpoint stamps trackerRevertedAt/By into workflow.context.
              const liveSlice = data.workflow.context?.[tc.stepId];
              const live =
                liveSlice && typeof liveSlice === 'object'
                  ? (liveSlice as { trackerRevertedAt?: unknown; trackerRevertedBy?: unknown })
                  : undefined;
              const revertedAt =
                typeof live?.trackerRevertedAt === 'string'
                  ? live.trackerRevertedAt
                  : (tc.revertedAt ?? null);
              const revertedBy =
                typeof live?.trackerRevertedBy === 'string'
                  ? live.trackerRevertedBy
                  : (tc.revertedBy ?? null);
              return (
                <TrackerChangesPanel
                  changes={tc}
                  revertedAt={revertedAt}
                  revertedBy={revertedBy}
                  canRevert={canApprove}
                  isReverting={revertTracker.isPending}
                  onRevert={() => {
                    if (workflowId) revertTracker.mutate({ workflowId, stepId: tc.stepId });
                  }}
                  className="mb-4"
                />
              );
            })()}
            {/* Speaker card gate only: the two-phase QC panel (draft grid →
                finalized preview). It owns the chosenCandidate + highlightColor
                form values, so the generic selects are hidden from the
                FormRenderer schema while it's mounted. */}
            {cardCandidates != null && cardGate != null && workflowId != null && (
              <CardCandidatesPanel
                manifest={cardCandidates}
                cardState={cardGate}
                workflowId={workflowId}
                subjectName={data.workflow.displayName}
                disabled={!canApprove}
                isRegenerating={regenerate.isPending}
                onRegenerate={onRegenerateCards}
                onForceRegenerate={onForceRegenerateCards}
                onFinalize={onFinalizeCard}
                onBuildCard={onBuildCard}
                onBackToFinal={onBackToFinalPortrait}
                onBack={onBackToDrafts}
                className="mb-4"
              />
            )}
            {(() => {
              const raw: unknown = gateData.schema;
              const fields =
                cardCandidates != null && Array.isArray(raw)
                  ? (raw as unknown[]).filter((f) => {
                      const key = (f as { key?: unknown } | null)?.key;
                      return key !== 'chosenCandidate' && key !== 'highlightColor';
                    })
                  : raw;
              return !Array.isArray(fields) || fields.length > 0 ? (
                <FormRenderer
                  schema={normalizeGateSchema(fields)}
                  readonlyFields={gateData.readonly ?? []}
                />
              ) : null;
            })()}
          </>
        ) : (
          <Empty
            icon={<Icon name="info" size={20} />}
            title="Unknown gate type"
            description="The gate metadata is missing or malformed. Contact #pixel-dev."
          />
        )}

        {/* Sticky action bar (handoff §Specifics line 162) — full-bleed within page padding */}
        <div className={cnActionBar()}>
          <div className="flex items-center gap-2 text-[12px] text-text-muted">
            <Icon name="user" size={14} />
            <span>
              Reviewing as: <strong className="text-text">{me?.name ?? 'You'}</strong>
            </span>
            {me?.role && (
              <Badge variant="role" className="ml-1">
                {me.role.replace('_', ' ')}
              </Badge>
            )}
          </div>
          <div className="flex-1" />
          {canApprove ? (
            <>
              {(isSpeakerGate || step.stepId === 'welcome_email') &&
                isEmailGate &&
                step.status === 'waiting_approval' && (
                  <Button
                    type="button"
                    size="default"
                    variant="ghost"
                    onClick={() =>
                      setRegenTargets([
                        {
                          workflowId: workflowId ?? '',
                          stepIndex,
                          label: `${stepName} · ${data.workflow.displayName}`,
                        },
                      ])
                    }
                    disabled={approve.isPending || skip.isPending || reject.isPending}
                    title="Rebuild this draft from the current templates and defaults — your edits here are dropped; nothing is sent"
                  >
                    <Icon name="retry" size={12} />
                    Regenerate
                  </Button>
                )}
              {isRejected && (
                <Button
                  type="button"
                  size="default"
                  variant="ghost"
                  onClick={onResubmit}
                  disabled={resubmit.isPending}
                  title="Re-open this gate as waiting_approval without re-submitting edits"
                >
                  Mark waiting
                </Button>
              )}
              {(isEmailGate || step.stepId === 'card_assets' || step.stepId === 'reg_code') && (
                <Button
                  type="button"
                  size="default"
                  variant="ghost"
                  onClick={() => setShowSkipConfirm(true)}
                  disabled={skip.isPending}
                  title={
                    step.stepId === 'card_assets'
                      ? 'Skip the speaker card — nothing is generated or published from here'
                      : step.stepId === 'reg_code'
                        ? 'Skip the registration code — for agreements with no passes (nothing is created in Personatech)'
                        : 'Mark this email as sent without sending it from here (already sent outside the system)'
                  }
                >
                  Skip
                </Button>
              )}
              <Button
                type="button"
                size="default"
                variant="default"
                onClick={() => setShowReject(true)}
                disabled={reject.isPending}
              >
                Reject
              </Button>
              {/* D3: live placeholder warning — click lands on the first token. */}
              {speakerPlaceholders.length > 0 && (
                <button
                  type="button"
                  onClick={jumpToFirstPlaceholder}
                  className="inline-flex items-center gap-1.5 rounded-full border border-waiting-border bg-waiting-soft px-3 py-1.5 text-[12px] font-semibold text-waiting"
                >
                  <Icon name="alert" size={12} />
                  {speakerPlaceholders.length} placeholder
                  {speakerPlaceholders.length === 1 ? '' : 's'} to fill — jump to first
                </button>
              )}
              <Button
                type="submit"
                size="default"
                variant="primary"
                disabled={
                  approve.isPending ||
                  form.formState.isSubmitting ||
                  speakerPlaceholders.length > 0 ||
                  cardBuildRequired
                }
                title={
                  speakerPlaceholders.length > 0
                    ? `Fill the remaining placeholder${speakerPlaceholders.length === 1 ? '' : 's'} before sending: ${speakerPlaceholders.join(', ')}`
                    : cardBuildRequired
                      ? 'Build the speaker card first — you approve it once you can see it (or choose "Skip card")'
                      : undefined
                }
              >
                {isEmailGate ? (
                  <>
                    <Icon name="send" size={12} />
                    Approve &amp; Send Email
                  </>
                ) : (
                  <>
                    Approve
                    <Icon name="chevron-right" size={12} />
                  </>
                )}
              </Button>
            </>
          ) : (
            <span className="text-[12px] text-text-muted">
              You don&apos;t have permission to approve this gate. This is a read-only view.
            </span>
          )}
        </div>
      </form>

      <RejectModal
        open={showReject}
        onOpenChange={setShowReject}
        onConfirm={onReject}
        isSubmitting={reject.isPending}
      />
      <ApproveConfirm
        open={showApproveConfirm}
        onOpenChange={setShowApproveConfirm}
        onConfirm={() => {
          setShowApproveConfirm(false);
          onApprove(form.getValues());
        }}
        recipient={String(form.watch('to') ?? '')}
        subject={String(form.watch('subject') ?? '')}
        unfilledPlaceholders={uniquePlaceholderKeys(
          findPlaceholders(String(form.watch('body') ?? '')),
        )}
        attachmentCount={
          Array.isArray(form.watch('attachments'))
            ? (form.watch('attachments') as unknown[]).length
            : 0
        }
        isSubmitting={approve.isPending}
      />
      <RegenerateDialog
        targets={regenTargets}
        userId={me?.id}
        onClose={(outcomes) => {
          setRegenTargets(null);
          if (!outcomes.some((o) => o.ok)) return;
          // The dialog cleared the localStorage draft and awaited the workflow
          // refetch, so `defaultValues` here already reflects the fresh server
          // copy. Reset explicitly: if the redraft happened to render
          // byte-identical to before, defaultsKey wouldn't change and the
          // draft-reset effect would never fire (F14 — a draft always wins).
          draft.clear();
          form.reset(defaultValues);
        }}
      />
      <SkipConfirm
        open={showSkipConfirm}
        onOpenChange={setShowSkipConfirm}
        onConfirm={onSkip}
        isSubmitting={skip.isPending}
        body={
          step?.stepId === 'card_assets'
            ? 'Use this when the speaker card is being handled outside Pixel. Nothing is generated or published from here.'
            : step?.stepId === 'reg_code'
              ? 'Use this when the sponsorship includes no passes (e.g. Hosted Meetings only). No registration code is created in Personatech and the Tracker registration cells stay blank.'
              : undefined
        }
        note={
          step?.stepId === 'card_assets'
            ? 'The promotion email still needs a card on the monday board before it can be approved — upload one there if it was made elsewhere.'
            : step?.stepId === 'reg_code'
              ? 'The welcome email will leave out the registration-code block — you can double-check it at the next gate.'
              : undefined
        }
      />
    </FormProvider>
  );
}

/**
 * Sticky action bar Tailwind class string (handoff §Specifics line 162).
 * Pinned to the bottom safe-area; offset so it aligns to the right of the
 * sidebar at lg, the icon rail at md, and full-bleed at sm (D-3.2).
 *
 * @returns space-separated Tailwind class string
 * @sideEffects none
 * @idempotent yes
 */
function cnActionBar(): string {
  return [
    'fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-bg/80 backdrop-blur-md',
    'lg:left-[248px] md:left-14 sm:left-0',
    'flex items-center gap-3 px-9 py-3.5 sm:px-4 sm:py-3',
    'flex-wrap',
  ].join(' ');
}

/**
 * Inline draft autosave/restore that accepts a form instance directly
 * (rather than reading useFormContext). Equivalent to the standalone
 * useDraft hook but avoids the FormProvider-ordering issue when the page
 * computes its draft key BEFORE rendering FormProvider.
 *
 * @param workflowId workflow id (part of the key)
 * @param stepIndex zero-based step index (part of the key)
 * @param userId authenticated user id (part of the key — prevents cross-reviewer leakage)
 * @param form RHF form instance
 * @returns `{ clear }` — call after successful Approve/Reject/Resubmit
 * @sideEffects subscribes to form.watch; debounced writes to localStorage
 * @idempotent yes — repeated mounts converge on the same restored state
 */
function useGateDraft(
  workflowId: string | undefined,
  stepIndex: number,
  userId: string | undefined,
  form: UseFormReturn<Record<string, unknown>>,
  defaultValues: Record<string, unknown>,
  defaultsKey: string,
): { clear: () => void; hasDraft: boolean } {
  const k = useMemo(
    () =>
      workflowId && userId && !Number.isNaN(stepIndex) ? key(workflowId, stepIndex, userId) : null,
    [workflowId, stepIndex, userId],
  );
  // Tracks whether a restored draft is currently backing the editor. Drives
  // the truthful wording of the resubmit banner (F12): only claim "pre-filled
  // with your previous edits" when a draft actually survived.
  const [hasDraft, setHasDraft] = useState(false);
  // Fingerprint of the SERVER draft the reviewer is editing. The sending
  // mailbox is left out: it resolves a beat after load (and is the reviewer's
  // own pick), so including it would orphan every draft on the next visit.
  const base = useMemo(() => {
    const { sendFromConnectionId: _from, attachments: _files, ...server } = defaultValues;
    return JSON.stringify(server);
  }, [defaultValues]);
  const save = useDebouncedCallback((v: Record<string, unknown>) => {
    if (k) saveDraft(k, v, base);
  }, 500);

  // Single reset effect (F14): runs when the draft target (k) OR the server
  // defaults (defaultsKey, which changes on a background WS-driven refetch)
  // change. A live draft always wins over server defaults, so a refetch can't
  // silently discard the reviewer's in-progress edits — the previous design
  // had two competing reset effects and the defaults one clobbered the draft.
  useEffect(() => {
    // A draft only survives while the server draft it was edited on is still
    // current; a rebuild or Doc sync on the server wins (readDraftOnBase).
    const restored = k ? readDraftOnBase<Record<string, unknown>>(k, base) : null;
    setHasDraft(restored != null);
    // Draft wins PER FIELD; server defaults fill any field the draft doesn't
    // carry (e.g. `sendFromConnectionId` on drafts saved before the
    // multi-account Gmail feature) so the form state always matches what the
    // inputs visibly show.
    form.reset(restored ? { ...defaultValues, ...restored } : defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, defaultsKey]);

  // Save only after the reviewer actually edits something (watch `type` is
  // 'change' for user input; resets and programmatic setValue carry none), so
  // merely opening a gate never pins its current copy in the browser.
  // `attachments` is excluded: the base64 file payloads would blow the ~5 MB
  // localStorage quota (and silently break ALL draft saves for the key) —
  // files are cheap to re-attach after a reload.
  useEffect(() => {
    if (!k) return;
    const sub = form.watch((values, { type }) => {
      if (type !== 'change') return;
      const { attachments: _attachments, ...rest } = values as Record<string, unknown>;
      save(rest);
    });
    return () => sub.unsubscribe();
  }, [form, save, k]);

  return {
    clear: () => {
      if (k) clearDraft(k);
      setHasDraft(false);
    },
    hasDraft,
  };
}
